import { useState } from "react";

import { MenuSearchInput } from "common/components/MenuSearchInput";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuList,
  DropdownMenuTrigger,
} from "common/components/ui/DropdownMenu";
import FormField from "common/components/ui/FormField";
import ProfileImage from "common/components/ui/ProfileImage";
import SelectButton from "common/components/ui/SelectButton";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import useLinkedInEvents from "../../../datahooks/useLinkedInEvents";

import type { LinkedInEvent } from "../../../types";

import GroupDefaultImage from "assets/images/group-fallback.svg";

interface EventFormFieldProps {
  accountId: string;
  selectedEvent: LinkedInEvent | undefined;
  onSelect: (event: LinkedInEvent) => void;
}

export default function EventFormField({
  accountId,
  selectedEvent,
  onSelect,
}: EventFormFieldProps) {
  const isDesktop = useTwBreakpoint("lg");

  const { linkedInEvents, isLoadingLinkedInEvents } =
    useLinkedInEvents(accountId);

  const [inputValue, setInputValue] = useState("");

  let selectButtonLabel = "Select an event for your targeting";
  if (selectedEvent) {
    selectButtonLabel = selectedEvent.name;
  } else if (isLoadingLinkedInEvents) {
    selectButtonLabel = "Loading events...";
  }

  const filteredEvents = linkedInEvents
    ? linkedInEvents.filter((event) =>
        event.name.toLowerCase().includes(inputValue.toLowerCase()),
      )
    : [];

  return (
    <FormField
      label="Select an event"
      htmlFor=""
      hint="Select a LinkedIn event to collect its attendees as leads"
    >
      <DropdownMenu>
        <DropdownMenuTrigger asChild disabled={isLoadingLinkedInEvents}>
          <SelectButton
            size={isDesktop ? "md" : "lg"}
            hasSelection={!!selectedEvent}
            isLoading={isLoadingLinkedInEvents}
          >
            {selectButtonLabel}
          </SelectButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-[var(--radix-popper-anchor-width)]">
          {linkedInEvents?.length > 4 && (
            <MenuSearchInput
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
            />
          )}
          <DropdownMenuList>
            {linkedInEvents?.length === 0 ? (
              <p className="p-4 text-center text-black-500">
                You are not attending any LinkedIn events
              </p>
            ) : (
              filteredEvents.map((event) => {
                const { id, name, images, description } = event;
                const imageUrl = images.length
                  ? images[0].url
                  : GroupDefaultImage;

                return (
                  <DropdownMenuItem key={id} onSelect={() => onSelect(event)}>
                    <ProfileImage src={imageUrl} />
                    <div className="flex flex-col">
                      <h5 className="line-clamp-1 text-left text-button-sm">
                        {name}
                      </h5>
                      <p className="line-clamp-1 text-left text-xs text-black-500">
                        {description}
                      </p>
                    </div>
                  </DropdownMenuItem>
                );
              })
            )}
          </DropdownMenuList>
        </DropdownMenuContent>
      </DropdownMenu>
    </FormField>
  );
}
